/**
 * A table's user-set layout — column widths dragged on the header borders and an order dragged by
 * the headers — kept apart from the columns themselves, which a page defines, so the layout can be
 * saved (per table, in the account's layout preferences) and applied to whatever columns the page
 * shows next time. Pure, so the arithmetic is tested without a pointer.
 */

export interface TableLayout {
  /** Widths in px by column id; a column without one keeps the width the page gave it. */
  widths?: Record<string, number>;
  /** Column ids, first to last. Ids not listed follow in the page's order; unknown ids are ignored. */
  order?: string[];
}

/** The page's columns in the saved order, with any the page added since appended where it put them. */
export function applyOrder<T extends { id: string }>(columns: T[], order: string[] | undefined): T[] {
  if (!order || order.length === 0) return columns;
  const byId = new Map(columns.map((c) => [c.id, c]));
  const placed = new Set<string>();
  const out: T[] = [];
  for (const id of order) {
    const c = byId.get(id);
    if (c && !placed.has(id)) {
      out.push(c);
      placed.add(id);
    }
  }
  for (const c of columns) if (!placed.has(c.id)) out.push(c);
  return out;
}

/**
 * `ids` with the item at `from` dropped at insertion point `to` (an index into the list as it was,
 * 0 = before the first item, `ids.length` = after the last).
 */
export function moveColumn(ids: string[], from: number, to: number): string[] {
  const next = ids.slice();
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return ids;
  next.splice(to > from ? to - 1 : to, 0, moved);
  return next;
}

/** The CSS grid track a column takes: its saved width, or the one the page gave it. */
export function trackFor(column: { id: string; width: string }, layout: TableLayout | undefined): string {
  const px = layout?.widths?.[column.id];
  return px != null && Number.isFinite(px) ? `${Math.round(px)}px` : column.width;
}

/** Where the pointer would drop a dragged header: after every cell whose midpoint it has passed. */
export function dropIndex(cellRects: Array<{ left: number; width: number }>, x: number, firstMovable: number): number {
  let at = firstMovable;
  for (let k = firstMovable; k < cellRects.length; k++) {
    const r = cellRects[k]!;
    if (x > r.left + r.width / 2) at = k + 1;
  }
  return at;
}
